import { useEffect, useState } from 'react';
import { Form, Modal } from 'antd';
import { Box } from '@mui/material';
import { IdcardOutlined } from '@ant-design/icons';
import Swal from 'sweetalert2';
import EditableValueField from './EditableValueField';
import SectionCard from './SectionCard';
import api from '../../../Api/axiosInstance';
import { encryptBody, parseEncryptedResponse } from './kycCrypto';
import Data from '../../../Data/Data.json';
import {
  CHAMPS_DATE_IDENTITE_KYC,
  CHAMPS_DOCUMENT_IDENTITE_KYC,
  CHAMPS_IDENTITE_PROCURATION,
  CHAMPS_IDENTITE_SIGNATAIRE,
  CHAMPS_IMAGE_IDENTITE_KYC,
  CHAMPS_RADIO_IDENTITE_KYC,
  GROUPES_CHAMPS_IDENTITE_PROCURATION,
  GROUPES_CHAMPS_IDENTITE_SIGNATAIRE,
  LABELS_CHAMP_IDENTITE_KYC,
  type ChampIdentiteKyc,
  type DemandeKycIdentiteDto,
} from '../../../Models/DemandeKyc';

interface KycModifierIdentiteModalProps {
  open: boolean;
  type: 'SIGNATAIRE' | 'PROCURATION';
  numcli: string;
  codRel: string;
  // Valeurs actuelles connues (Nom/Prénom/CNI/Photo/Signature, issues des
  // tableaux déjà chargés) — les autres champs n'ont pas de source actuelle
  // confirmée côté backend pour l'instant, donc ancienne valeur vide (saisie
  // libre), sans empêcher leur modification.
  ancienneValeurs: Partial<Record<ChampIdentiteKyc, string>>;
  enregistrePar: string;
  onClose: () => void;
  onSubmitted: (idDemande: number | string) => void;
}

// Modification de l'identité propre d'un signataire/mandataire déjà lié à un
// compte (nom, téléphone, CNI...) — pas un ajout/retrait de lien. Même
// principe maker-checker que le reste du KYC, mais endpoint/DTO séparés
// (POST /Kyc/DemandeModificationIdentite, confirmé par le backend 2026-10-08).
export default function KycModifierIdentiteModal({
  open, type, numcli, codRel, ancienneValeurs, enregistrePar, onClose, onSubmitted,
}: KycModifierIdentiteModalProps) {
  const [form] = Form.useForm();
  const [busy, setBusy] = useState(false);

  const champs: ChampIdentiteKyc[] = type === 'SIGNATAIRE' ? CHAMPS_IDENTITE_SIGNATAIRE : CHAMPS_IDENTITE_PROCURATION;
  const groupes = type === 'SIGNATAIRE' ? GROUPES_CHAMPS_IDENTITE_SIGNATAIRE : GROUPES_CHAMPS_IDENTITE_PROCURATION;

  useEffect(() => {
    if (open) {
      const valeurs: Record<string, string> = {};
      champs.forEach((champ) => { valeurs[`NouvelleValeur_${champ}`] = ancienneValeurs[champ] ?? ''; });
      form.setFieldsValue(valeurs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, codRel]);

  function champFieldProps(champ: ChampIdentiteKyc) {
    if (CHAMPS_IMAGE_IDENTITE_KYC.includes(champ)) return { type: 'image' as const, form };
    if (CHAMPS_DOCUMENT_IDENTITE_KYC.includes(champ)) return { type: 'document' as const, form };
    const radioOptions = CHAMPS_RADIO_IDENTITE_KYC[champ];
    if (radioOptions) return { type: 'radio' as const, radioOptions, form };
    return { type: CHAMPS_DATE_IDENTITE_KYC.includes(champ) ? ('date' as const) : ('texte' as const) };
  }

  async function handleOk() {
    const values = await form.validateFields().catch(() => null);
    if (!values) return;

    const details = champs
      .filter((champ) => {
        const nouvelle = values[`NouvelleValeur_${champ}`];
        if (!nouvelle) return false;
        return String(nouvelle) !== String(ancienneValeurs[champ] ?? '');
      })
      .map((champ) => ({ Champ: champ, NouvelleValeur: values[`NouvelleValeur_${champ}`] }));

    if (details.length === 0) {
      Swal.fire({ title: 'Attention', text: 'Veuillez modifier au moins un champ.', icon: 'warning' });
      return;
    }

    const titre = type === 'SIGNATAIRE' ? "Modifier l'identité du signataire" : "Modifier l'identité du mandataire";
    const confirmation = await Swal.fire({
      title: titre,
      text: `Confirmez-vous l'envoi de ${details.length} champ(s) modifié(s) ?`,
      showCancelButton: true,
      color: 'green',
      confirmButtonText: 'Oui',
      confirmButtonColor: 'green',
      cancelButtonColor: 'darkred',
      cancelButtonText: 'Non',
      showLoaderOnConfirm: true,
      icon: 'question',
    });
    if (!confirmation.isConfirmed) return;

    setBusy(true);
    try {
      const dto: DemandeKycIdentiteDto = {
        NUMCLI: numcli,
        TypeRelation: type === 'SIGNATAIRE' ? 'SIGNATAIRE_MODIFICATION' : 'PROCURATION_MODIFICATION',
        CodRel: codRel,
        EnregistrePar: enregistrePar,
        Details: details,
      };
      const response = await api.post(Data.ApiUrl.KycDemandeModificationIdentite, encryptBody(dto), { headers: { 'Content-Type': 'application/json' } });
      const payload = parseEncryptedResponse(response);
      if (payload && payload.StatutCode === 200) {
        Swal.fire({ title: 'Succès', text: `Demande enregistrée. Référence : ${payload.Result}.`, icon: 'success' });
        onSubmitted(payload.Result);
      } else {
        Swal.fire({ title: 'Erreur', text: payload?.Message ?? 'La demande a été refusée.', icon: 'error' });
      }
    } catch (error: any) {
      const payload = parseEncryptedResponse(error?.response);
      Swal.fire({ title: 'Erreur', text: payload?.Message ?? "Impossible de soumettre la demande.", icon: 'error' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      title={type === 'SIGNATAIRE' ? "Modifier l'identité du signataire" : "Modifier l'identité du mandataire"}
      onCancel={onClose}
      onOk={handleOk}
      okText="Enregistrer"
      okButtonProps={{ style: { color: '#fff' }, loading: busy }}
      cancelText="Annuler"
      destroyOnClose
      width={960}
    >
      <Form form={form} labelAlign="left" autoComplete="off">
        {/* Exactement 2 blocs par ligne (gauche/droite), même principe que
            KycIdentiteRelationPage.tsx — pas auto-fit, pour garder les blocs
            média groupés sous les 2 blocs texte quelle que soit la largeur. */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, alignItems: 'start' }}>
          {groupes.map((groupe) => (
            <SectionCard key={groupe.titre} icon={<IdcardOutlined style={{ fontSize: 14 }} />} title={groupe.titre} color="#124b7a">
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 1 }}>
                {groupe.champs.map((champ) => (
                  <EditableValueField
                    key={champ}
                    label={LABELS_CHAMP_IDENTITE_KYC[champ]}
                    name={`NouvelleValeur_${champ}`}
                    ancienneValeur={ancienneValeurs[champ] ?? ''}
                    editable
                    {...champFieldProps(champ)}
                  />
                ))}
              </Box>
            </SectionCard>
          ))}
        </Box>
      </Form>
    </Modal>
  );
}
